import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Paper, useTheme, Tooltip } from '@mui/material'
import { lightBlue } from '@mui/material/colors'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { noop } from 'lodash'
import { startOfDay, isBefore, differenceInDays } from 'date-fns'
import { EventCalendar, EventList } from 'components/configured-service/accordions/EventsAccordion/components'
import { selectActionType, selectBusinessLine, selectBusinessLineType } from 'data/configured-service/configuredServiceDetailsSelectors'
import T from 'T'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { findRecurrenceOptionByKey } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'
import { buildRecurrenceEvents } from 'components/configured-service/EditServiceRecurrenceDialog/utils/eventBuilder'
import {
  categorizeEventChanges,
  filterEventsForMonth,
  generateEventStatusesByDay,
  groupEventsByDay,
} from 'components/configured-service/EditServiceRecurrenceDialog/utils/eventProcessing'

const DraftServiceEventsPreview = ({
  open,
  startDate = new Date(),
  formData,
  defaultValues,
  recurrenceOptions,
  serviceData = {},
  existingEvents = [],
  isNewService = false,
}) => {
  const theme = useTheme()

  const formattedStartDate = useMemo(() => formatDateToBEFormatDateFns(startDate), [startDate])

  const displayStartDate = useMemo(() => {
    const today = startOfDay(new Date())
    const providedStartDate = startDate ? startOfDay(new Date(startDate)) : today
    return isBefore(providedStartDate, today) ? today : providedStartDate
  }, [startDate])

  const [selectedDate, setSelectedDate] = useState(displayStartDate)

  useEffect(() => {
    if (displayStartDate) {
      setSelectedDate(displayStartDate)
    }
  }, [displayStartDate])

  const baselineRef = useRef(null)

  const {
    dayOfTheMonth,
    dayOfTheWeek,
    daysOfWeek: selectedDays = {},
    eventInstanceType,
    eventOccurrence,
    recurrenceFrequency,
    serviceInterval: serviceIntervalKey,
  } = formData

  const {
    dayOfTheMonth: baselineDayOfTheMonth,
    dayOfTheWeek: baselineDayOfTheWeek,
    daysOfWeek: baselineSelectedDays = {},
    eventInstanceType: baselineEventInstanceType,
    eventOccurrence: baselineEventOccurrence,
    recurrenceFrequency: baselineFrequency,
    serviceInterval: baselineServiceIntervalKey,
  } = defaultValues

  const actionType = useMemo(() => selectActionType(serviceData), [serviceData])
  const baselineServiceInterval = findRecurrenceOptionByKey(recurrenceOptions, baselineServiceIntervalKey)
  const businessLine = useMemo(() => selectBusinessLine(serviceData), [serviceData])
  const businessLineType = useMemo(() => selectBusinessLineType(serviceData), [serviceData])
  const serviceInterval = findRecurrenceOptionByKey(recurrenceOptions, serviceIntervalKey)

  const serviceEventData = useMemo(
    () => ({
      actionType,
      businessLine,
      businessLineType,
    }),
    [actionType, businessLine, businessLineType]
  )

  const horizonDays = useMemo(() => {
    const today = startOfDay(new Date())
    const start = startOfDay(new Date(startDate))
    const daysDiff = isBefore(start, today) ? differenceInDays(today, start) : 0
    return 90 + daysDiff
  }, [startDate])

  const buildEvents = useCallback(
    (frequency, interval, daysOfWeek, events, eventData, nonWeeklyParams = {}) => {
      try {
        if (!frequency || !interval) {
          return []
        }

        return buildRecurrenceEvents({
          frequency,
          serviceIntervalData: interval,
          selectedDays: daysOfWeek,
          existingEvents: events,
          serviceEventData: eventData,
          startDate,
          horizonDays,
          nonWeeklyParams,
        })
      } catch (error) {
        // Log error to aid in debugging issues with event generation
        // eslint-disable-next-line no-console
        console.error('Failed to build recurrence events in DraftServiceEventsPreview.buildEvents', error)
        return []
      }
    },
    [formattedStartDate, horizonDays, serviceEventData, existingEvents]
  )

  const currentEvents = useMemo(() => {
    if (!recurrenceFrequency || !serviceInterval) {
      return []
    }

    return buildEvents(recurrenceFrequency, serviceInterval, selectedDays, existingEvents, serviceEventData, {
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
    })
  }, [
    buildEvents,
    dayOfTheMonth,
    dayOfTheWeek,
    eventInstanceType,
    eventOccurrence,
    existingEvents,
    recurrenceFrequency,
    selectedDays,
    serviceEventData,
    serviceInterval,
  ])

  useEffect(() => {
    if (isNewService) {
      baselineRef.current = []
      return
    }
    if (open && !baselineRef.current) {
      baselineRef.current = buildEvents(
        baselineFrequency ?? recurrenceFrequency,
        baselineServiceInterval ?? serviceInterval,
        baselineSelectedDays ?? selectedDays,
        existingEvents,
        serviceEventData
      )
    }
    if (!open) {
      baselineRef.current = null
    }
  }, [
    baselineFrequency,
    baselineSelectedDays,
    baselineServiceInterval,
    buildEvents,
    existingEvents,
    isNewService,
    open,
    recurrenceFrequency,
    selectedDays,
    serviceEventData,
    serviceInterval,
  ])

  const baselineEvents = useMemo(() => {
    if (isNewService) return []
    return buildEvents(
      baselineFrequency ?? recurrenceFrequency,
      baselineServiceInterval ?? serviceInterval,
      baselineSelectedDays ?? selectedDays,
      existingEvents,
      serviceEventData,
      {
        eventInstanceType: baselineEventInstanceType ?? eventInstanceType,
        eventOccurrence: baselineEventOccurrence ?? eventOccurrence,
        dayOfTheWeek: baselineDayOfTheWeek ?? dayOfTheWeek,
        dayOfTheMonth: baselineDayOfTheMonth ?? dayOfTheMonth,
      }
    )
  }, [
    baselineDayOfTheMonth,
    baselineDayOfTheWeek,
    baselineEventInstanceType,
    baselineEventOccurrence,
    baselineFrequency,
    baselineSelectedDays,
    baselineServiceInterval,
    buildEvents,
    dayOfTheMonth,
    dayOfTheWeek,
    eventInstanceType,
    eventOccurrence,
    existingEvents,
    isNewService,
    recurrenceFrequency,
    selectedDays,
    serviceEventData,
    serviceInterval,
  ])

  const eventCategories = useMemo(() => {
    return categorizeEventChanges(baselineEvents, currentEvents)
  }, [baselineEvents, currentEvents])

  const { allEvents: mockEvents } = eventCategories

  const futureEvents = useMemo(() => {
    const today = startOfDay(new Date())
    return mockEvents.filter(event => {
      const eventDate = event.serviceDateObj || new Date(event.serviceDate)
      return !isBefore(startOfDay(eventDate), today)
    })
  }, [mockEvents])

  const currentMonthEvents = useMemo(() => {
    return filterEventsForMonth(futureEvents, selectedDate)
  }, [futureEvents, selectedDate])

  const currentMonthCounts = useMemo(() => {
    const existing = currentMonthEvents.filter(eventItem => eventItem._status === 'existing').length
    const newItems = currentMonthEvents.filter(eventItem => eventItem._status === 'new').length
    const removed = currentMonthEvents.filter(eventItem => eventItem._status === 'removed').length

    return { existingCount: existing, newCount: newItems, removedCount: removed }
  }, [currentMonthEvents])

  const { existingCount, newCount, removedCount } = currentMonthCounts

  const eventsByDay = useMemo(() => {
    return groupEventsByDay(currentMonthEvents)
  }, [currentMonthEvents])

  const eventStatusesByDay = useMemo(() => {
    return generateEventStatusesByDay(currentMonthEvents)
  }, [currentMonthEvents])

  const selectedDayEvents = useMemo(() => {
    const selectedDayOfMonth = selectedDate.getDate()
    const dayEvents = eventsByDay[selectedDayOfMonth] || []
    return [...dayEvents].sort((a, b) => new Date(a.serviceDate) - new Date(b.serviceDate))
  }, [eventsByDay, selectedDate])

  const handleDateChange = newDate => {
    setSelectedDate(newDate)
  }

  const handleMonthChange = newMonth => {
    setSelectedDate(prev => {
      const day = prev.getDate()
      const year = newMonth.getFullYear()
      const month = newMonth.getMonth()

      // Create date with same day in new month
      const newDate = new Date(year, month, day)

      // If it rolled over (e.g. Feb 31 -> March 3), clamp it to the last day of the target month
      if (newDate.getMonth() !== month) {
        return new Date(year, month + 1, 0)
      }
      return newDate
    })
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ backgroundColor: lightBlue[50], p: 2, border: `1px solid ${theme.palette.info.light}`, borderRadius: '4px 4px 0 0' }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
          {T.EVENTS_PREVIEW}
          <Tooltip title={T.GENERATING_EVENTS_PREVIEW} sx={{ color: 'text.secondary' }}>
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 0, backgroundColor: 'white', borderRadius: '0 0 4px 4px' }}>
        <Box display="flex" width="100%" flexWrap="wrap">
          {/* Calendar */}
          <Box width="100%">
            <EventCalendar
              eventStatusesByDay={eventStatusesByDay}
              eventsByDay={eventsByDay}
              isLoading={false}
              onDateChange={handleDateChange}
              onMonthChange={handleMonthChange}
              selectedDate={selectedDate}
            />
          </Box>

          {/* Event List */}
          <Box width="100%">
            <Box
              sx={{
                border: '2px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                p: 0.5,
              }}
            >
              <Typography variant="body2" fontWeight={500} mb={1}>
                {T.EVENTS}
              </Typography>
              <Box display="flex" gap={1.5}>
                {!isNewService && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.primary.main, borderRadius: '2px' }} />
                    <Typography variant="caption">Existing -{existingCount}</Typography>
                  </Box>
                )}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.info.light, borderRadius: '2px' }} />
                  <Typography variant="caption">New -{newCount}</Typography>
                </Box>
                {!isNewService && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.grey[300], borderRadius: '2px' }} />
                    <Typography variant="caption">Removed -{removedCount}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <EventList
              control={{}}
              getAvailableActionsForEvent={noop}
              hideAddEventButton
              isDraft={false}
              isPreview
              onAddEvent={noop}
              onDelete={noop}
              onEdit={noop}
              onUndoDelete={noop}
              selectedDayEvents={selectedDayEvents}
              serviceActionsById={{}}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

DraftServiceEventsPreview.propTypes = {
  open: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  formData: PropTypes.shape({
    dayOfTheMonth: PropTypes.number,
    dayOfTheWeek: PropTypes.string,
    daysOfWeek: PropTypes.object,
    eventInstanceType: PropTypes.string,
    eventOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurrenceFrequency: PropTypes.string,
    serviceInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  defaultValues: PropTypes.shape({
    dayOfTheMonth: PropTypes.number,
    dayOfTheWeek: PropTypes.string,
    daysOfWeek: PropTypes.object,
    eventInstanceType: PropTypes.string,
    eventOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurrenceFrequency: PropTypes.string,
    serviceInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  existingEvents: PropTypes.array,
  isNewService: PropTypes.bool,
  recurrenceOptions: PropTypes.array.isRequired,
  serviceData: PropTypes.object,
}

export default DraftServiceEventsPreview
